import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import StatusNotificationsModal from './StatusNotificationsModal';
import { isReliableParkDataStatus, readParkDataStatus } from '../utils/parkDataStatus';

/**
 * GlobalNotifications
 * Composant global qui surveille les notifications de tous les parcs
 * et affiche le bouton flottant de notifications au niveau de l'application
 */
const GlobalNotifications = () => {
  const [allNotifications, setAllNotifications] = useState([]);
  const [readNotifications, setReadNotifications] = useState(new Set());
  const location = useLocation();

  // Liste des parcs supportés pour les notifications
  // eslint-disable-next-line no-unused-vars
  const supportedParks = useMemo(() => [
    'parc-asterix',
    'walt-disney-studios',
    'disneyland-paris',
    'efteling',
    'phantasialand',
    'universal-studios',
    'islands-of-adventure',
    'europa-park',
    'toverland',
    'futuroscope',
    'le-pal',
    'nigloland',
    'plopsaland-de-panne',
    'gardaland',
    'mirabilandia',
    'movie-park-germany',
    'parque-de-atracciones-de-madrid',
    'portaventura',
    'ferrari-land',
    'energylandia',
    'grona-lund',
    'bellewaerde',
    'bobbejaanland',
    'seaworld-orlando',
    'busch-gardens-tampa',
    'legoland-florida',
    'knotts-berry-farm',
    'lotte-world',
    'typhoon-lagoon',
    'blizzard-beach',
    'rulantica',
    'epic-universe',
    'magic-kingdom',
    'epcot',
    'hollywood-studios',
    'animal-kingdom',
    'tokyo-disneyland',
    'shanghai-disneyland',
    'hong-kong-disneyland-park',
    'disneyland-california',
    'disney-california-adventure'
  ], []);

  // Migration : nettoyer les anciennes clés de notification
  useEffect(() => {
    const keysToRemove = [];
    const todayKey = new Date().toISOString().slice(0, 10);
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key
        && (key.startsWith('downStart:') || key.startsWith('reopenAt:') || key.startsWith('currentDownAttractions:'))
      ) {
        const keyParts = key.split(':');
        const keyDate = keyParts[keyParts.length - 1];
        if (/^\d{4}-\d{2}-\d{2}$/.test(keyDate) && keyDate !== todayKey) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  // Fonction pour récupérer toutes les notifications depuis localStorage
  const getAllNotifications = () => {
    const notifications = [];

    // Récupérer toutes les notifications persistées
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('notification:')) {
        try {
          const persistedData = JSON.parse(localStorage.getItem(key));
          const now = Date.now();

          // Calculer les minutes seulement si since n'est pas null
          let minutes = 0;
          if (persistedData.since) {
            const sinceTime = Date.parse(persistedData.since);
            minutes = Math.max(0, Math.floor((now - sinceTime) / 60000));
          }

          // Vérifier l'expiration pour les réouvertures (seulement si since existe)
          if (persistedData.type === 'reopen' && persistedData.since && minutes >= 30) {
            // Supprimer les notifications de réouverture expirées
            localStorage.removeItem(key);
            continue;
          }

          // Vérifier l'expiration pour les réouvertures récentes (sans since)
          if (persistedData.type === 'reopen' && persistedData.isRecentlyReopened && !persistedData.since) {
            const persistedTime = new Date(persistedData.persistedAt).getTime();
            const now = Date.now();
            const minutesElapsed = (now - persistedTime) / (1000 * 60);

            // Supprimer les notifications récemment réouvertes après 30 minutes (comme les autres réouvertures)
            if (minutesElapsed >= 30) {
              localStorage.removeItem(key);
              continue;
            }
          }

          // Vérifier si les données du parc sont fiables
          const parkDataStatus = readParkDataStatus(persistedData.parkSlug);
          if (!isReliableParkDataStatus(parkDataStatus)) {
            // Supprimer les notifications pour les parcs avec des données non fiables
            localStorage.removeItem(key);
            continue;
          }

          notifications.push({
            ...persistedData,
            minutes
          });
        } catch (error) {
          // Supprimer les données corrompues
          localStorage.removeItem(key);
        }
      }
    }

    return notifications;
  };

  // Fonction pour marquer les notifications comme lues
  const markNotificationsAsRead = () => {
    const newReadSet = new Set(readNotifications);
    allNotifications.forEach(notification => {
      newReadSet.add(notification.key);
    });
    setReadNotifications(newReadSet);
    // Persister l'état des notifications lues
    localStorage.setItem('readNotifications', JSON.stringify([...newReadSet]));
  };

  // Charger l'état des notifications lues au démarrage
  useEffect(() => {
    const savedReadNotifications = localStorage.getItem('readNotifications');
    if (savedReadNotifications) {
      try {
        setReadNotifications(new Set(JSON.parse(savedReadNotifications)));
      } catch (error) {
        console.warn('Erreur lors du chargement des notifications lues:', error);
      }
    }
  }, []);

  // Mettre à jour les notifications au chargement et périodiquement
  useEffect(() => {
    const updateNotifications = () => {
      const notifications = getAllNotifications();
      setAllNotifications(notifications);

      // Si de nouvelles notifications arrivent, elles ne sont pas lues
      const newUnreadKeys = notifications
        .filter(n => !readNotifications.has(n.key))
        .map(n => n.key);

      if (newUnreadKeys.length > 0) {
        // Il y a de nouvelles notifications non lues
        // Le bouton réapparaîtra automatiquement
      }
    };

    // Mise à jour initiale immédiate
    updateNotifications();

    // Écouter les changements de localStorage pour les notifications
    const handleStorageChange = (e) => {
      if (e.key && (e.key.startsWith('notification:') || e.key.startsWith('parkDataStatus:'))) {
        updateNotifications();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Mise à jour périodique pour les compteurs de minutes et nettoyage
    const interval = setInterval(updateNotifications, 60000); // Toutes les minutes

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [readNotifications]);

  // Déterminer le parc actuel depuis l'URL
  const currentParkSlugs = useMemo(() => {
    const path = location.pathname;
    if (path === '/') {
      return ['disneyland-paris'];
    }
    if (path.startsWith('/studios')) {
      return ['walt-disney-studios'];
    }
    if (path.startsWith('/shows') || path.startsWith('/restaurants')) {
      // Pages communes aux deux parcs Disney
      return ['disneyland-paris', 'walt-disney-studios'];
    }
    // Pour les autres parcs, extraire depuis l'URL
    const parkMatch = path.match(/^\/([^/]+)/);
    const parkSlug = parkMatch ? parkMatch[1].replace(/-/g, '-') : null;
    return parkSlug ? [parkSlug] : [];
  }, [location.pathname]);

  // Filtrer les notifications par parc(s) actuel(s)
  const parkNotifications = currentParkSlugs.length > 0
    ? allNotifications.filter(n => currentParkSlugs.includes(n.parkSlug))
    : [];

  // Calculer les notifications non lues pour ce parc
  const unreadNotifications = parkNotifications.filter(n => !readNotifications.has(n.key));


  return (
    <StatusNotificationsModal
      notifications={parkNotifications}
      unreadNotifications={unreadNotifications}
      onMarkAsRead={markNotificationsAsRead}
    />
  );
};

export default GlobalNotifications;